import { IconTrash } from '@tabler/icons-react';
import type { BlockVolume } from '@xver/apis/v1/block-storage/model';
import { TypeToConfirmModal } from '@xver/components/Atoms';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useBlockStorageErrorText,
    useBlockVolumeMutations,
} from '../hooks/useBlockVolumeMutations';

interface DeleteVolumeModalProps {
    /** null = closed. */
    volume: BlockVolume | null;
    onClose: () => void;
    onDeleted: () => void;
}

export default function DeleteVolumeModal({ volume, onClose, onDeleted }: DeleteVolumeModalProps) {
    const { t } = useTranslation('blockStorage');
    const errorText = useBlockStorageErrorText();
    const { deleteVolume } = useBlockVolumeMutations();
    const { reset, isPending, error } = deleteVolume;
    const name = volume?.name ?? '';

    useEffect(() => {
        if (volume) reset();
    }, [volume, reset]);

    return (
        <TypeToConfirmModal
            opened={!!volume}
            onClose={onClose}
            title={t('delete.title', { name })}
            expected={name}
            inputLabel={t('delete.inputLabel')}
            inputDescription={t('delete.inputDescription', { name })}
            confirmLabel={t('delete.confirm')}
            confirmIcon={<IconTrash size={16} stroke={1.5} aria-hidden="true" />}
            isSubmitting={isPending}
            error={error ? errorText(error) : undefined}
            onConfirm={() => {
                if (volume) deleteVolume.mutate(volume, { onSuccess: onDeleted });
            }}
        >
            <p className="text-sm text-fg/78">{t('delete.body', { name })}</p>
        </TypeToConfirmModal>
    );
}
